import { AxiosError, AxiosResponse } from 'axios';
import api from '../api';
import { SORT_ORDER } from '../constants/filter.constant';
import { DefaultResponse, Institution } from '../interfaces/index';
import { ELEMENT_STATUS, ELEMENT_TYPE, Element, Metadata } from './element.api';
import { ResponseGetProcedure } from './procedure.api';
import { ImageElementProps } from '@vindr/ui/src/components/common/StructuredReport/components/elements/image/ImageElement';

const TEMPLATE_STRUCTURE_REPORT = '/ris/structure-report/v1/templates';

export interface ParamsGetTemplateStructureReport {
  codes?: string[];
  created_at_from?: string;
  created_at_to?: string;
  creator_ids?: string[];
  ids?: string[];
  institution_ids?: string[];
  limit?: number;
  name?: string;
  offset?: number;
  sort_field?: '' | 'code' | 'name' | 'created_at' | 'updated_at';
  sort_order?: SORT_ORDER;
  updated_at_from?: string;
  updated_at_to?: string;
}

export enum STRUCTURE_REPORT_VISIBILITY {
  PRIVATE = 'PRIVATE',
  PUBLIC = 'PUBLIC',
}

export interface StructureReport {
  code: string;
  created_at: string;
  created_by: string;
  id: string;
  institution_ids: string[];
  metadata: Metadata;
  modality: string[];
  institution_id: string;
  layout_id: string;
  institution: Institution;
  procedure: null | ResponseGetProcedure['data'][0];
  name: string;
  procedure_id: string;
  sections: Section[];
  status: ELEMENT_STATUS;
  updated_at: string;
  updated_by: string;
  visibility: STRUCTURE_REPORT_VISIBILITY;
}

export interface Section {
  id: string;
  created_at: string;
  created_by: string;
  LastUpdatedAt: string;
  LastUpdatedBy: string;
  status: number;
  report_template_id: string;
  type: ELEMENT_TYPE.STRUCTURE;
  name: SECTION_TYPE;
  content: null | string;
  report_element_ids: string[];
  elements: (Element | ImageElementProps)[];
  UpdatedAt: string;
  UpdatedBy: string;
  value: null | string;
}

export interface ResponseGetStructureTemplates extends DefaultResponse {
  data: StructureReport[];
}
export interface ResponseGetStructureTemplate extends DefaultResponse {
  data: StructureReport;
}
export const getStructureTemplates = async (
  params: ParamsGetTemplateStructureReport
) => {
  const response = (await api({
    url: TEMPLATE_STRUCTURE_REPORT + '/search',
    method: 'POST',
    data: params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetStructureTemplates>;

  return response.data;
};
export const getStructureTemplate = async (id: string) => {
  const response = (await api({
    url: TEMPLATE_STRUCTURE_REPORT + '/' + id,
    method: 'GET',
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetStructureTemplates>;

  return response.data;
};

export enum SECTION_TYPE {
  CLINICAL_INFORMATION = 'CLINICAL_INFORMATION',
  TECHNIQUE = 'TECHNIQUE',
  FINDINGS = 'FINDINGS',
  COMPARISON = 'COMPARISON',
  IMPRESSION = 'IMPRESSION',
}

export interface ParamsCreateStructureReport {
  name: string;
  code: string;
  visibility: STRUCTURE_REPORT_VISIBILITY;
  procedure_id?: string;
  institution_id?: string;
  modality?: string;
  metadata?: Metadata;
  sections?: {
    name: string;
    content: string;
    section_type?: SECTION_TYPE;
    element_ids?: string[];
  }[];
}

export interface ErrorCreateStructureReportAlreadyExists {
  error_code: '';
  error_message: 'Element(s) id not exist';
  server_time: number;
}

export interface ErrorCreateNotKnownSectionType {
  error_code: 'EMSG_20004';
  error_message: 'Input Error';
  server_time: number;
  meta: {
    'Sections[0].Type'?: 'Section type value can only be either CLINICAL_INFORMATION, COMPARISON, TECHNIQUE, FINDINGS or IMPRESSION';
    Code?: 'Code must not be null';
    Name?: 'Name must not be null';
    Visibility?: 'Visibility value can only be either PRIVATE or PUBLIC';
    message?: 'This user not allow to create PUBLIC visibility template';
  };
}

export interface ResponseCreateStructureReport extends DefaultResponse {
  data: StructureReport;
  meta: {
    code: 'SCD_31006';
    message: 'Success';
  };
}

export const createStructureReport = async (
  data: ParamsCreateStructureReport
) => {
  try {
    const response = (await api({
      url: TEMPLATE_STRUCTURE_REPORT,
      method: 'POST',
      data,
    })) as AxiosResponse<ResponseCreateStructureReport>;

    return response?.data;
  } catch (error) {
    throw error as AxiosError<
      ErrorCreateStructureReportAlreadyExists | ErrorCreateNotKnownSectionType
    >;
  }
};

export const updateStructureReport = async (
  id: string,
  data: ParamsCreateStructureReport
) => {
  const response = (await api({
    url: `${TEMPLATE_STRUCTURE_REPORT}/${id}`,
    method: 'PATCH',
    data,
  })) as AxiosResponse<ResponseCreateStructureReport>;

  return response.data;
};

export interface ResponseDeleteStructureReport extends DefaultResponse {
  meta: {
    code: 'SCD_31007';
    message: 'Success';
  };
}

export interface ErrorDeleteNotFoundTemplate
  extends Omit<DefaultResponse, 'count' | 'data'> {
  error_code: 'EMSG_0001';
  error_message: 'Template not found';
}

export const deleteStructureReport = async (id: string) => {
  try {
    return (await api({
      url: `${TEMPLATE_STRUCTURE_REPORT}/${id}`,
      method: 'DELETE',
    })) as AxiosResponse<ResponseDeleteStructureReport>;
  } catch (e) {
    const error = e as AxiosError<ErrorDeleteNotFoundTemplate>;

    throw error?.response?.data;
  }
};
